'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { InternalNetworkCredentialDto } from '@terra/shared-contracts-internal-network';
import { toasterApi } from '../../toast/ToastProvider';
import {
  canVerifyInternalNetworkCredential,
  resolveInternalNetworkCredentialVerificationState,
  toInternalNetworkCredentialVerificationState,
  type InternalNetworkCredentialType,
  type InternalNetworkCredentialVerificationResult,
  type InternalNetworkCredentialVerificationState,
} from './internal-credentials.model';

const addId = ({ ids, id }: { ids: Set<string>; id: string }) =>
  new Set(ids).add(id);

const removeId = ({ ids, id }: { ids: Set<string>; id: string }) =>
  new Set([...ids].filter((existingId) => existingId !== id));

const omitKey = ({
  record,
  key,
}: {
  record: Record<string, InternalNetworkCredentialVerificationState>;
  key: string;
}) =>
  Object.fromEntries(
    Object.entries(record).filter(([recordKey]) => recordKey !== key),
  );

export const useInternalNetworkCredentialVerification = ({
  canVerify,
  verifyBlockReasonLabel,
  onVerify,
}: {
  canVerify: boolean;
  verifyBlockReasonLabel: string | null;
  onVerify: (
    credentialId: string,
  ) => Promise<InternalNetworkCredentialVerificationResult>;
}) => {
  const t = useTranslations('designSystem.networkSettings.internalCredentials');
  const tCredentials = useTranslations('designSystem.credentials');
  const [testingCredentialIds, setTestingCredentialIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [verificationStateByCredentialId, setVerificationStateByCredentialId] =
    useState<Record<string, InternalNetworkCredentialVerificationState>>({});

  const setVerificationState = useCallback(
    ({
      credentialId,
      state,
    }: {
      credentialId: string;
      state: InternalNetworkCredentialVerificationState;
    }) => {
      setVerificationStateByCredentialId((previous) => ({
        ...previous,
        [credentialId]: state,
      }));
    },
    [],
  );

  const resolveVerificationState = useCallback(
    (
      credential: InternalNetworkCredentialDto,
    ): InternalNetworkCredentialVerificationState =>
      resolveInternalNetworkCredentialVerificationState({
        credential,
        isTesting: testingCredentialIds.has(credential.id),
        localState: verificationStateByCredentialId[credential.id],
      }),
    [testingCredentialIds, verificationStateByCredentialId],
  );

  const testCredential = useCallback(
    async ({
      credentialId,
      credentialType,
    }: {
      credentialId: string;
      credentialType?: InternalNetworkCredentialType;
    }) => {
      if (!canVerifyInternalNetworkCredential({ canVerify, credentialType })) {
        toasterApi.error({
          title: t('testUnavailableTitle'),
          description:
            verifyBlockReasonLabel ?? t('testUnavailableDescription'),
        });

        return;
      }

      setTestingCredentialIds((previous) =>
        addId({ ids: previous, id: credentialId }),
      );
      setVerificationState({ credentialId, state: { status: 'VERIFYING' } });
      toasterApi.success({ title: tCredentials('test.starting') });

      try {
        const result = await onVerify(credentialId);

        setVerificationState({
          credentialId,
          state: toInternalNetworkCredentialVerificationState(result),
        });

        if (!result.success) {
          toasterApi.error({
            title: tCredentials('test.failed'),
            description: result.error ?? undefined,
          });
        }
      } catch (error: unknown) {
        console.error(
          new Error('Failed to verify internal network credential'),
          error,
        );
        setVerificationState({ credentialId, state: { status: 'FAILED' } });
        toasterApi.error({ title: tCredentials('test.error') });
      } finally {
        setTestingCredentialIds((previous) =>
          removeId({ ids: previous, id: credentialId }),
        );
      }
    },
    [
      canVerify,
      onVerify,
      setVerificationState,
      t,
      tCredentials,
      verifyBlockReasonLabel,
    ],
  );

  const clearVerificationState = useCallback((credentialId: string) => {
    setVerificationStateByCredentialId((previous) =>
      omitKey({ record: previous, key: credentialId }),
    );
  }, []);

  return {
    testingCredentialIds,
    resolveVerificationState,
    testCredential,
    clearVerificationState,
  };
};
